import type { IUserSettingsCmsTerms, SubmittalDragType } from 'app/services/userSettingsApi';

/**
 * Small shared vocabulary for the Submittal tab — the three library sources, the
 * `model` → library-type map, the CMS-document derivation, the two jQuery
 * measurements the legacy view depends on, and (U-d) the drag payload the
 * library hands the format rail.
 */

/** `data-type` on the three chips, the three `.submittal-row` panes and the
 * three `.fields-count` labels. `self.activeDefinedsField` (`submittal.js:113`)
 * starts at `'user'`. */
export type SubmittalLibraryType = 'user' | 'cms' | 'application';

/**
 * `submittal.js:114-118` `self.fields` — the format row's `model` (the content
 * type's lower-case model name, straight off the serializer) mapped onto the
 * library that owns it. A row whose `model` is absent from this map (a
 * freshly-created placeholder, `model: null`) makes `rowClick` fall through
 * without touching the library at all.
 */
export const SUBMITTAL_FIELD_MODELS: Record<string, SubmittalLibraryType> = {
  mastersubmittalpredefinedfield: 'application',
  submittaluserdefinedfield: 'user',
  cmsahuterms: 'cms',
};

/**
 * ★ U-d — everything `createNewField` (`submittal.js:786-831`) reads off a
 * dropped library item, and nothing else.
 *
 * Legacy does not pass an object anywhere: `createNewField` receives `ui.item`
 * (the jQuery-UI helper that landed in the sortable, cloned from the library
 * anchor) and pulls four ATTRIBUTES off it. Which of the four are present
 * depends entirely on which of the three `<a>` templates was dragged, and the
 * absences are load-bearing — they are what makes a CMS drop send no
 * `object_id` and a user drop send no `cms_field_name`:
 *
 *   library   data-type       data-id        data-name       data-text
 *   ────────  ──────────────  ─────────────  ──────────────  ─────────────
 *   User      —  (→ fallback) `{{id}}`       —               `{{name}}`
 *   CMS       `cms_terms`     —              `{{value}}`     `{{label}}`
 *   App       `app_defined`   `{{id}}`       —               `{{name}}`
 *
 * The fields below are therefore optional exactly where the attribute is
 * missing, and are read with the same `getAttribute` semantics (a missing
 * attribute is `undefined`, never `''` — `JSON.stringify` only drops the
 * former).
 */
export interface SubmittalDragPayload {
  /** `e.attr("data-type") || "user_defined"` (`:788`) — note the `||`, so an
   * empty attribute falls back too. */
  type: SubmittalDragType;
  /** `e.attr("data-name")` (`:789`) → the PATCH's `cms_field_name`. */
  name?: string;
  /** `e.attr("data-id")` (`:790`) → the PATCH's `object_id`, as a STRING. */
  id?: string;
  /** `e.attr("data-text")` (`:791`) → the label the hand-injected rail row
   * shows until the full re-render replaces it with the server's `name`. */
  text: string;
}

/**
 * `createNewField`'s `models` map (`:803-807`) — the drag `type` mapped onto the
 * content type's model name, i.e. the INVERSE of `SUBMITTAL_FIELD_MODELS`. Used
 * only to fill `data-model` on the hand-injected row, so that `rowClick`'s
 * cross-highlight works on it before the server has said what the row is.
 */
export const SUBMITTAL_DRAG_MODELS: Record<SubmittalDragType, string> = {
  user_defined: 'submittaluserdefinedfield',
  cms_terms: 'cmsahuterms',
  app_defined: 'mastersubmittalpredefinedfield',
};

/** One row of the CMS library / one read-only detail pane. */
export interface SubmittalCmsDocument {
  /** `<key>_name` — the display label, e.g. "Shipping Terms & Conditions". */
  label: string;
  /** The terms key itself, e.g. `shipping_terms`. Used as `data-cms`,
   * `data-name` and the `.{{value}}_textarea` class. */
  value: string;
  /** `cms_terms_<value>` — the anchor/pane id stem. */
  key: string;
}

/**
 * `submittal.js:137-146`, transcribed literally.
 *
 * The CMS library is not a list endpoint: it is DERIVED from the terms object by
 * walking `Object.keys(terms)` and keeping every key `k` whose `k + '_name'`
 * sibling is truthy. Six keys qualify on mfg 1, in serializer-declaration order
 * (sales / shipping / warranty / ahu guide specs / condensing unit / packaged
 * unit) — `Object.keys` preserves JSON insertion order for non-index keys, so
 * the order is the serializer's `fields` tuple.
 *
 * ★ An mfg with fewer configured `*_name` labels legitimately shows fewer rows
 * (discovery §15.2 — only mfg 1 was observable on dev3). Do not hard-code six.
 */
export function deriveSubmittalCmsDocuments(
  terms: IUserSettingsCmsTerms | undefined
): SubmittalCmsDocument[] {
  if (!terms) return [];
  const rec = terms as unknown as Record<string, unknown>;
  const documents: SubmittalCmsDocument[] = [];
  Object.keys(rec).forEach((key) => {
    const label = rec[`${key}_name`];
    if (label) {
      documents.push({ label: String(label), value: key, key: `cms_terms_${key}` });
    }
  });
  return documents;
}

/** The raw HTML body for one CMS document — `render()` `.append()`s it into
 * `.{{value}}_textarea` (`submittal.js:183-187`). Missing/empty → `''`. */
export function submittalCmsBody(
  terms: IUserSettingsCmsTerms | undefined,
  value: string
): string {
  if (!terms) return '';
  const body = (terms as unknown as Record<string, unknown>)[value];
  return typeof body === 'string' ? body : '';
}

/**
 * `$(el).offset().top` — DOCUMENT-relative, i.e. viewport rect + page scroll.
 * Only used by `mouseover` below.
 */
function jqOffsetTop(el: Element): number {
  return el.getBoundingClientRect().top + window.pageYOffset;
}

/**
 * `$(el).height()` — jQuery returns the CONTENT height (padding and border
 * excluded) regardless of `box-sizing`, which `clientHeight` does not.
 */
export function jqHeight(el: Element): number {
  const style = window.getComputedStyle(el);
  return (
    el.clientHeight -
    (parseFloat(style.paddingTop) || 0) -
    (parseFloat(style.paddingBottom) || 0)
  );
}

/**
 * `submittal.js:245-255` `mouseover`, ported literally — including its bugs,
 * because the observable output is a class name that legacy CSS may key off.
 *
 * The handler is bound to `.delete-btn-content` (the trash `<button>` on every
 * library row and every format row — 301 of them) and is supposed to flip a
 * Bootstrap dropdown upwards when the button is within 50 px of the bottom of
 * its scroll container. Three things about it:
 *
 *  1. It reads `event.target`, not `currentTarget`. Hovering the nested
 *     `<i class="icon-trash">` therefore passes the ICON, which carries no
 *     `data-parent`, so `$(null)` is an empty set, `el.offset()` is `undefined`
 *     and `el.height()` is `undefined` → `undefined - pos < 50` is `NaN < 50` →
 *     `false` → the class is REMOVED. Only hovering the button's own padding
 *     ever adds it.
 *  2. It mixes coordinate systems: `getBoundingClientRect().top` is
 *     viewport-relative while `offset().top` is document-relative. Harmless
 *     here only because this page's `<body>` never scrolls.
 *  3. ★ Q20 — the dropdown it is positioning does not exist. The button is a
 *     `data-toggle="dropdown"` with no `.dropdown-menu` sibling anywhere, so
 *     `.top` decorates a menu that is never rendered.
 *
 * Returns whether `.top` should be present, so the caller can keep the class in
 * React's declarative `className` instead of fighting it imperatively.
 */
export function deleteButtonWantsTop(target: HTMLElement): boolean {
  const parentSelector = target.getAttribute('data-parent');
  const container = parentSelector ? document.querySelector(parentSelector) : null;
  if (!container) return false; // the `NaN < 50` branch — always false
  const pos = target.getBoundingClientRect().top - jqOffsetTop(container);
  return jqHeight(container) - pos < 50;
}
